import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { DatePicker } from '../components/common/DatePicker';
import { KpiCard } from '../components/common/KpiCard';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { fmtEur } from '../lib/constants';
import { Euro, ClipboardList, Receipt, TrendingUp, FileText, FileSpreadsheet, Users, AlertTriangle, Armchair, ClipboardCheck, Printer } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { StatusPill } from '../components/common/StatusPill';
import { useNavigate } from 'react-router-dom';
import { useRealtime } from '../lib/realtime';
import { useCash } from '../context/CashContext';

export default function ReportsPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('day');
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState('');
  const [live, setLive] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);
  const [zReports, setZReports] = useState([]);
  const { session: cashSession } = useCash();
  const navigate = useNavigate();

  const reportParams = () => (period === 'custom'
    ? { period: 'custom', date_from: dateFrom, date_to: dateTo }
    : { period });

  const load = async () => {
    if (period === 'custom' && (!dateFrom || !dateTo)) return;
    const { data } = await api.get('/reports/sales', { params: reportParams() });
    setData(data);
  };
  useEffect(() => { load(); }, [period, dateFrom, dateTo]);

  const loadLive = async () => {
    try {
      const [s, o] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/orders', { params: { active: true } }),
      ]);
      setLive(s.data);
      setActiveOrders(o.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadLive();
    const t = setInterval(loadLive, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRealtime((msg) => {
    if (msg && ['orders', 'tables', 'payments', 'kds'].includes(msg.event)) loadLive();
    if (msg && msg.event === 'cash') loadZReports();
  });

  const loadZReports = async () => {
    try { const { data } = await api.get('/cash/reports'); setZReports(data); } catch (e) { /* silent */ }
  };
  useEffect(() => { loadZReports(); }, []);

  const downloadCash = async (kind, sid) => {
    setDownloading('cash');
    try {
      const url = kind === 'x' ? '/cash/report/x/pdf' : `/cash/report/z/${sid}/pdf`;
      const res = await api.get(url, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = u; a.download = kind === 'x' ? 'informe_x.pdf' : 'informe_z.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('reportsCash.downloadError'));
    } finally { setDownloading(''); }
  };

  const printCash = async (kind, sid) => {
    try {
      const url = kind === 'x' ? '/cash/report/x/print' : `/cash/report/z/${sid}/print`;
      const { data } = await api.post(url);
      toast[data.queued ? 'success' : 'info'](data.queued ? t('reportsCash.printQueued') : t('reportsCash.printNoPrinter'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('reportsCash.downloadError'));
    }
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.get(`/reports/sales/${fmt}`, { params: reportParams(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = fmt === 'pdf' ? 'report_vendite.pdf' : 'report_vendite.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t('reports.reportDownloaded'));
    } catch (e) { toast.error(t('reports.exportError')); } finally { setDownloading(''); }
  };

  const colors = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];
  const methodData = data ? [
    { name: t('reports.cash'), value: data.method_breakdown.cash || 0 },
    { name: t('reports.card'), value: data.method_breakdown.card || 0 },
    { name: t('reports.mixed'), value: data.method_breakdown.mixed || 0 },
  ].filter((d) => d.value > 0) : [];

  return (
    <div className="p-4 md:p-6">
      <PageHeader title={t('reports.title')} subtitle={t('reports.subtitle')} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList>
              <TabsTrigger value="day" data-testid="report-period-day">{t('reports.today')}</TabsTrigger>
              <TabsTrigger value="week">{t('reports.week')}</TabsTrigger>
              <TabsTrigger value="month">{t('reports.month')}</TabsTrigger>
              <TabsTrigger value="custom" data-testid="report-period-custom">{t('reports.custom', { defaultValue: 'Personalizzato' })}</TabsTrigger>
            </TabsList>
          </Tabs>
          {period === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <DatePicker value={dateFrom} max={dateTo || undefined} onChange={setDateFrom} className="w-44" data-testid="report-date-from" />
              <span className="text-muted-foreground text-sm">→</span>
              <DatePicker value={dateTo} min={dateFrom || undefined} onChange={setDateTo} className="w-44" data-testid="report-date-to" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => download('pdf')} disabled={!!downloading} data-testid="reports-export-pdf-button" className="rounded-xl"><FileText className="h-4 w-4 mr-2" /> PDF</Button>
          <Button variant="outline" onClick={() => download('excel')} disabled={!!downloading} data-testid="reports-export-excel-button" className="rounded-xl"><FileSpreadsheet className="h-4 w-4 mr-2" /> Excel</Button>
        </div>
      </div>

      {!data ? <div className="text-muted-foreground">{t('reports.loading')}</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard label={t('reports.revenue')} value={fmtEur(data.revenue)} icon={Euro} accent="free" sub={data.label} />
            <KpiCard label={t('reports.orders')} value={data.orders_count} icon={ClipboardList} accent="primary" />
            <KpiCard label={t('reports.avgTicket')} value={fmtEur(data.avg_ticket)} icon={Receipt} accent="accent" />
            <KpiCard label={t('reports.margin')} value={fmtEur(data.margin)} icon={TrendingUp} accent="ordered" sub={t('reports.marginPct', { pct: data.margin_pct })} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <Card className="p-5 lg:col-span-2">
              <div className="font-semibold mb-4">{t('reports.revenueTrend')}</div>
              {data.daily_series.length === 0 ? <div className="h-60 flex items-center justify-center text-muted-foreground text-sm">{t('reports.noDataPeriod')}</div> : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data.daily_series}>
                    <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.4} /><stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12 }} formatter={(v) => fmtEur(v)} />
                    <Area type="monotone" dataKey="revenue" stroke="hsl(var(--chart-1))" fill="url(#rev)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
            <Card className="p-5">
              <div className="font-semibold mb-4">{t('reports.paymentMethods')}</div>
              {methodData.length === 0 ? <div className="h-60 flex items-center justify-center text-muted-foreground text-sm">{t('reports.noData')}</div> : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={methodData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {methodData.map((e, i) => <Cell key={i} fill={colors[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12 }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <Card className="p-5 mt-4">
            <div className="font-semibold mb-4">{t('reports.topProducts')}</div>
            {data.top_products.length === 0 ? <div className="text-sm text-muted-foreground py-6">{t('reports.noData')}</div> : (
              <ResponsiveContainer width="100%" height={Math.max(200, data.top_products.length * 38)}>
                <BarChart data={data.top_products} layout="vertical" margin={{ left: 30 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip cursor={{ fill: 'hsl(var(--muted))' }} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12 }} />
                  <Bar dataKey="qty" radius={[0, 6, 6, 0]} fill="hsl(var(--chart-2))" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Incassi per giornata (aperta/chiusa) separati per tipo di pagamento */}
          <Card className="p-5 mt-4">
            <div className="font-semibold mb-4">{t('reports.dailyTakings', { defaultValue: 'Incassi per giornata' })}</div>
            {(!data.daily_breakdown || data.daily_breakdown.length === 0) ? (
              <div className="text-sm text-muted-foreground py-6">{t('reports.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reports-daily-breakdown">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4 font-medium">{t('reports.day', { defaultValue: 'Giornata' })}</th>
                      <th className="py-2 pr-4 font-medium">{t('reports.opened', { defaultValue: 'Apertura' })}</th>
                      <th className="py-2 pr-4 font-medium">{t('reports.closed', { defaultValue: 'Chiusura' })}</th>
                      <th className="py-2 pr-4 font-medium text-right">{t('reports.cash')}</th>
                      <th className="py-2 pr-4 font-medium text-right">{t('reports.card')}</th>
                      <th className="py-2 font-medium text-right">{t('reports.revenue')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.daily_breakdown.map((d, i) => (
                      <tr key={i} className="border-b border-border/60 last:border-0">
                        <td className="py-2 pr-4 font-medium">{(d.date || '-')}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{d.opened_at ? d.opened_at.slice(0, 16).replace('T', ' ') : '-'}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{d.closed_at ? d.closed_at.slice(0, 16).replace('T', ' ') : (d.status === 'open' ? t('reports.dayOpen', { defaultValue: 'Aperta' }) : '-')}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{fmtEur(d.cash)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{fmtEur(d.card)}</td>
                        <td className="py-2 text-right font-semibold tabular-nums">{fmtEur(d.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Informes de Caja (X / Z) */}
          <Card className="p-5 mt-4" data-testid="reports-cash-reports">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <div className="font-semibold flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> {t('reportsCash.title')}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t('reportsCash.subtitle')}</div>
              </div>
              {cashSession ? (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="rounded-xl" disabled={downloading === 'cash'} onClick={() => downloadCash('x')} data-testid="reports-informe-x-pdf">
                    <FileText className="h-4 w-4 mr-2" /> {t('reportsCash.informeX')}
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => printCash('x')} data-testid="reports-informe-x-print">
                    <Printer className="h-4 w-4 mr-2" /> {t('reportsCash.print')}
                  </Button>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground rounded-full bg-muted px-3 py-1">{t('reportsCash.noOpenDay')}</span>
              )}
            </div>

            {zReports.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6">{t('reportsCash.noZ')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reports-z-list">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4 font-medium">Z</th>
                      <th className="py-2 pr-4 font-medium">{t('reportsCash.businessDay')}</th>
                      <th className="py-2 pr-4 font-medium">{t('reportsCash.closed')}</th>
                      <th className="py-2 pr-4 font-medium">{t('reportsCash.user')}</th>
                      <th className="py-2 pr-4 font-medium text-right">{t('reports.revenue')}</th>
                      <th className="py-2 font-medium text-right">{t('reportsCash.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zReports.map((z) => (
                      <tr key={z.session_id} className="border-b border-border/60 last:border-0" data-testid={`reports-z-row-${z.session_id}`}>
                        <td className="py-2 pr-4 font-mono font-semibold text-primary">{z.z_label}</td>
                        <td className="py-2 pr-4">{z.business_day}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{z.hasta}</td>
                        <td className="py-2 pr-4">{z.usuario_cierre}</td>
                        <td className="py-2 pr-4 text-right font-semibold tabular-nums">{fmtEur(z.total)}</td>
                        <td className="py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" title={t('reportsCash.informeX').replace('X', 'Z')} onClick={() => downloadCash('z', z.session_id)} data-testid={`reports-z-pdf-${z.session_id}`}>
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" title={t('reportsCash.print')} onClick={() => printCash('z', z.session_id)} data-testid={`reports-z-print-${z.session_id}`}>
                              <Printer className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Situazione attuale (LIVE) — widget operativi spostati dalla Dashboard */}
          <div className="mt-8 mb-3 flex items-center gap-2" data-testid="reports-live-section">
            <h2 className="font-display text-xl md:text-2xl font-black tracking-tight">{t('reports.currentSituation')}</h2>
            <span className="inline-flex items-center gap-2 rounded-full bg-pos-free/10 text-pos-free px-3 py-1 text-xs font-bold" data-testid="reports-live-pill">
              <span className="h-2 w-2 rounded-full bg-pos-free animate-pulse" /> LIVE
            </span>
          </div>

          {!live ? (
            <div className="text-muted-foreground text-sm">{t('reports.loadingLive')}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <KpiCard label={t('reports.occupiedTables')} value={`${live.occupied_tables}/${live.total_tables}`} icon={Users} accent="primary" sub={t('reports.freeTablesSub', { count: live.free_tables })} testId="reports-kpi-occupied-tables" />
                <KpiCard label={t('reports.lowStock')} value={live.low_stock_count} icon={AlertTriangle} accent="bill" sub={t('reports.lowStockSub')} testId="reports-kpi-low-stock" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                <Card className="p-5">
                  <div className="font-semibold mb-4 flex items-center gap-2"><Armchair className="h-4 w-4" /> {t('reports.roomStatus')}</div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{t('reports.free')}</span><span className="font-display font-semibold text-lg text-pos-free">{live.free_tables}</span></div>
                    <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{t('reports.occupied')}</span><span className="font-display font-semibold text-lg text-pos-occupied">{live.occupied_tables}</span></div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                      <div className="bg-pos-free h-full" style={{ width: `${(live.free_tables / Math.max(1, live.total_tables)) * 100}%` }} />
                      <div className="bg-pos-occupied h-full" style={{ width: `${(live.occupied_tables / Math.max(1, live.total_tables)) * 100}%` }} />
                    </div>
                  </div>
                </Card>

                <Card className="p-5 lg:col-span-2">
                  <div className="font-semibold mb-4">{t('reports.activeOrders')}</div>
                  {activeOrders.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">{t('reports.noActiveOrders')}</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {activeOrders.map((o) => (
                        <button key={o.id} onClick={() => navigate(`/ordine/${o.table_id}`)} className="w-full flex items-center justify-between py-3 hover:bg-muted/50 rounded-lg px-2 transition-colors text-left" data-testid={`reports-active-order-${o.id}`}>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold w-20">{o.table_name}</span>
                            <StatusPill status={o.status === 'sent' ? 'ordered' : 'occupied'} />
                            <span className="text-sm text-muted-foreground">{(o.items || []).length} {t('reports.items')}</span>
                          </div>
                          <span className="font-display font-semibold tabular">{fmtEur(o.total)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
